/**
 * "Unterschriften-Mappe" mode: when switched on, the user logs in against
 * ownCloud (via the OIDC IdP) and the PDFs in their /in folder are offered
 * one by one ("Nächstes", which wraps around from the last back to the
 * first, so the user can keep skipping through the folder). A document's optional summary ("<name>.pdf.txt") pops up when
 * hovering over the name/"Nächstes" area (rendered as Markdown). A PDF that gets signed is stored in
 * the /out folder instead of being downloaded, and the unsigned original and
 * its .txt are deleted from /in; PDFs that are only skipped stay untouched.
 *
 * This only decides *which* PDF gets loaded and where the result goes. Each
 * PDF is handed to the normal PDF-loading path (hooks.openPdf), so the
 * signing flow itself is exactly the same as for a manually picked file.
 */
import { AuthRequired, clearSession, handleRedirect, hasSession, startLogin, type MappeConfig } from "./oidc";
import { renderMarkdown } from "./markdown";
import { deleteInput, fetchNotifyConfig, fetchPdf, fetchSummary, listInput, storeSigned } from "./webdav";
import { notifyWebhook } from "../api";

export interface MappeHooks {
  /** Loads the file like a manually selected PDF. */
  openPdf(file: File): Promise<void>;
  /** Unloads the current PDF (nothing left to sign). */
  clearPdf(): void;
  setStatus(text: string): void;
  setLoading(active: boolean): void;
}

export interface Delivery {
  /** true: the signed PDF was stored in ownCloud, the caller must not download it. */
  handled: boolean;
  /** Extra text for the status line. */
  note?: string;
}

// Survives the redirect to the IdP and back; per tab, like the tokens.
const ACTIVE_KEY = "signit.mappe.active";
const SUMMARY_UNREADABLE = "(Zusammenfassung konnte nicht gelesen werden)";

let deliverFn: ((pdf: Blob, filename: string) => Promise<Delivery>) | null = null;

/**
 * Called after signing. In Mappe mode it moves the signed PDF to ownCloud and
 * cleans up /in; otherwise (or if that fails) it reports handled=false and
 * the caller downloads the PDF as usual, so a signed document is never lost.
 */
export async function deliverSigned(pdf: Blob, filename: string): Promise<Delivery> {
  return deliverFn ? deliverFn(pdf, filename) : { handled: false };
}

async function loadConfig(): Promise<MappeConfig | null> {
  try {
    const resp = await fetch("/api/v1/config");
    return ((await resp.json()) as { mappe?: MappeConfig }).mappe ?? null;
  } catch {
    return null; // config unreachable -- feature stays off
  }
}

function el<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

export async function initMappe(hooks: MappeHooks): Promise<void> {
  const loaded = await loadConfig();
  if (!loaded) return;
  const cfg: MappeConfig = loaded;

  const switchBox = el<HTMLDivElement>("mappe-switch");
  const ownCloudLink = el<HTMLAnchorElement>("mappeLink");
  const toggle = el<HTMLInputElement>("mappeToggle");
  const stateLabel = el<HTMLElement>("mappeState");
  const panel = el<HTMLDivElement>("mappe-panel");
  const docLabel = el<HTMLParagraphElement>("mappe-doc");
  const infoIcon = el<HTMLSpanElement>("mappe-info");
  const summaryEl = el<HTMLDivElement>("mappe-summary");
  const nextBtn = el<HTMLButtonElement>("mappe-next");
  const dropZone = el<HTMLDivElement>("pdf-drop-zone");
  const pdfInput = el<HTMLInputElement>("pdfFile");

  let docs: string[] = [];
  let summaryFiles = new Map<string, string>(); // pdf name -> its .txt name
  let notifyFiles = new Map<string, string>(); // pdf name -> its .json webhook config name
  let index = 0;
  let active = false;
  // Name of the PDF currently loaded for signing; null when nothing is.
  let current: string | null = null;
  // A login that just completed but is still rejected by ownCloud must not
  // send the user around the login redirect again and again.
  let justLoggedIn = false;

  function setActiveUi(on: boolean): void {
    active = on;
    toggle.checked = on;
    stateLabel.textContent = on ? "An" : "Aus";
    panel.hidden = !on;
    // The manual picker is replaced by the folder while the mode is on; a
    // hidden but `required` input would otherwise block form submission.
    dropZone.hidden = on;
    pdfInput.required = !on;
  }

  // --- summary popup ------------------------------------------------------
  // The summary is Markdown, rendered into sanitized DOM nodes (never innerHTML).
  let hasSummary = false;
  function setSummary(text: string | null): void {
    const rendered = text ? renderMarkdown(text) : null;
    // Markdown that renders to nothing visible (e.g. only an image) is no summary.
    hasSummary = !!rendered && (rendered.textContent ?? "").trim() !== "";
    summaryEl.replaceChildren(...(hasSummary && rendered ? [rendered] : []));
    summaryEl.hidden = true;
    infoIcon.hidden = !hasSummary;
  }

  function showSummary(visible: boolean): void {
    summaryEl.hidden = !(visible && hasSummary);
  }

  panel.addEventListener("mouseenter", () => showSummary(true));
  panel.addEventListener("mouseleave", () => showSummary(false));
  panel.addEventListener("focusin", () => showSummary(true));
  panel.addEventListener("focusout", () => showSummary(false));
  // Touch screens have no hover: tapping the name / the (i) toggles the popup.
  for (const target of [docLabel, infoIcon]) {
    target.addEventListener("click", () => {
      if (window.matchMedia("(hover: none)").matches) showSummary(!!summaryEl.hidden);
    });
  }

  // --- errors -------------------------------------------------------------
  function fail(err: unknown): void {
    // fetch() rejects with a bare TypeError on network errors and CORS blocks.
    hooks.setStatus(
      err instanceof TypeError
        ? "Verbindung zu ownCloud fehlgeschlagen."
        : err instanceof Error
          ? err.message
          : String(err),
    );
  }

  async function onAuthRequired(): Promise<void> {
    if (justLoggedIn) {
      sessionStorage.removeItem(ACTIVE_KEY);
      clearSession();
      setActiveUi(false);
      hooks.setStatus("ownCloud hat die Anmeldung abgelehnt.");
      return;
    }
    await startLogin(cfg);
  }

  // --- navigation ---------------------------------------------------------
  // "Nächstes" cycles through the documents still waiting; it is only useless
  // when the one on screen is the only one left. (If nothing is loaded --
  // after signing, or a failed load -- any remaining document can be shown.)
  function updateNext(): void {
    nextBtn.disabled = docs.length <= (current !== null ? 1 : 0);
  }

  async function showDoc(i: number): Promise<void> {
    index = i;
    current = null;
    const name = docs[i];
    const txt = summaryFiles.get(name);
    docLabel.textContent = `Dokument ${i + 1} von ${docs.length}: ${name}`;
    setSummary(null);
    nextBtn.disabled = true;
    hooks.setLoading(true);
    try {
      const [file, summary] = await Promise.all([
        fetchPdf(cfg, name),
        txt
          ? fetchSummary(cfg, txt).catch((err) => {
              if (err instanceof AuthRequired) throw err;
              return SUMMARY_UNREADABLE;
            })
          : Promise.resolve(null),
      ]);
      setSummary(summary);
      hooks.setStatus("");
      await hooks.openPdf(file);
      current = name;
    } catch (err) {
      if (err instanceof AuthRequired) await onAuthRequired();
      else fail(err);
    } finally {
      hooks.setLoading(false);
      updateNext();
    }
  }

  async function activate(): Promise<void> {
    sessionStorage.setItem(ACTIVE_KEY, "1");
    setActiveUi(true);
    if (!hasSession()) {
      await startLogin(cfg);
      return;
    }
    docLabel.textContent = "Ordner wird gelesen...";
    setSummary(null);
    nextBtn.disabled = true;
    hooks.setLoading(true);
    try {
      const listing = await listInput(cfg);
      docs = listing.pdfs;
      summaryFiles = listing.summaries;
      notifyFiles = listing.notifyConfigs;
      justLoggedIn = false; // ownCloud accepted the token; later expiry may re-login
    } catch (err) {
      docLabel.textContent = "";
      if (err instanceof AuthRequired) await onAuthRequired();
      else fail(err);
      return;
    } finally {
      hooks.setLoading(false);
    }
    if (docs.length === 0) {
      docLabel.textContent = `Keine Dokumente in der Mappe gefunden.`;
      return;
    }
    await showDoc(0);
  }

  function deactivate(): void {
    sessionStorage.removeItem(ACTIVE_KEY);
    setActiveUi(false);
    current = null;
    setSummary(null);
  }

  // --- after signing ------------------------------------------------------
  deliverFn = async (pdf, filename) => {
    if (!active || current === null) return { handled: false };
    const name = current;
    const txt = summaryFiles.get(name);
    const json = notifyFiles.get(name);

    let stored: string;
    try {
      stored = await storeSigned(cfg, filename, pdf);
    } catch (err) {
      // Nothing was changed in ownCloud; let the caller download the PDF.
      const reason = err instanceof AuthRequired ? "Anmeldung abgelaufen" : err instanceof Error ? err.message : String(err);
      return { handled: false, note: `Ablage in ownCloud fehlgeschlagen (${reason}).` };
    }

    const problems: string[] = [];
    if (json) {
      try {
        const notify = await fetchNotifyConfig(cfg, json);
        await notifyWebhook(notify.url, notify.itemId, `${cfg.outFolder}/${stored}`);
      } catch {
        problems.push("Benachrichtigungsversand fehlgeschlagen");
      }
    }

    // The signed copy is safe in the out folder; only now remove the originals.
    for (const file of [name, ...(txt ? [txt] : []), ...(json ? [json] : [])]) {
      try {
        await deleteInput(cfg, file);
      } catch (err) {
        problems.push(err instanceof Error ? err.message : String(err));
      }
    }

    current = null;
    hooks.clearPdf();
    setSummary(null);
    // It is gone from the input folder, so it must not come around again:
    // drop it from the cycle (the next "Nächstes" then lands on its successor).
    docs.splice(index, 1);
    index -= 1;
    docLabel.textContent = `✓ ${name} - signiert`;
    updateNext();
    const done = `Signiert: ${stored} liegt in ${cfg.outFolder}.`;
    const next = docs.length === 0 ? " Alle Dokumente bearbeitet." : ' Weiter mit "Nächstes".';
    return {
      handled: true,
      note: problems.length ? `${done} Achtung: ${problems.join(" ")}` : done + next,
    };
  };

  toggle.addEventListener("change", () => {
    if (toggle.checked) void activate();
    else deactivate();
  });
  nextBtn.addEventListener("click", () => {
    if (docs.length > 0) void showDoc((index + 1) % docs.length);
  });

  // The mode name doubles as a link to the ownCloud root (the checkbox next to
  // it is a separate control).
  ownCloudLink.href = cfg.ocisUrl;
  switchBox.hidden = false;

  try {
    justLoggedIn = await handleRedirect(cfg);
  } catch (err) {
    sessionStorage.removeItem(ACTIVE_KEY);
    fail(err);
    return;
  }
  if (sessionStorage.getItem(ACTIVE_KEY) === "1") await activate();
}
